import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Upload, CheckCircle2, Loader2, Image as ImageIcon, Video, X } from 'lucide-react';
import { progressAPI, uploadAPI, mediaUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { celebrate } from '../lib/confetti';

const MOODS = [
  { key: 'mood_energy', label: 'Énergie' },
  { key: 'mood_motivation', label: 'Motivation' },
  { key: 'mood_hunger', label: 'Satiété' },
  { key: 'mood_digestion', label: 'Digestion' },
  { key: 'mood_sleep', label: 'Sommeil' },
];

const MoodRow = ({ label, value, onChange, testKey }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-sm font-medium text-foreground">{label}</span>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`mood-${testKey}-${n}`}
          onClick={() => onChange(n)}
          className={`w-7 h-7 rounded-full text-xs font-bold transition-all ${
            value >= n ? 'bg-rose-600 text-white scale-100' : 'bg-muted text-muted-foreground'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  </div>
);

export const ValidateDialog = ({ open, onOpenChange, challenge, onValidated }) => {
  const { user, updateLocalUser } = useAuth();
  const [level, setLevel] = useState(user?.level === 'avance' ? 'avance' : 'debutant');
  const [comment, setComment] = useState('');
  const [media, setMedia] = useState(null); // {url, media_type}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moods, setMoods] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setComment('');
      setMedia(null);
      setMoods({});
      setLevel(user?.level === 'avance' ? 'avance' : 'debutant');
    }
  }, [open, user]);

  if (!challenge) return null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.upload(file);
      setMedia(res);
      toast.success('Preuve ajoutée !');
    } catch (err) {
      toast.error("Échec de l'envoi du fichier");
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async () => {
    setSaving(true);
    try {
      const res = await progressAPI.validate({
        day_number: challenge.day_number,
        media_type: media?.media_type || null,
        proof_url: media?.url || null,
        comment: comment || null,
        ...moods,
      });
      // refresh local user points/streak
      if (user) updateLocalUser({ ...user, ...res.stats });
      celebrate();
      toast.success(`Défi validé ! +${res.points_earned} points 🎉`);
      onValidated?.(res);
      onOpenChange(false);
    } catch (err) {
      toast.error('La validation a échoué');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="validate-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-950 dark:text-red-50">
            Jour {challenge.day_number} · {challenge.title}
          </DialogTitle>
          <DialogDescription>{challenge.coach_message}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Level switch */}
          <div className="flex gap-2 p-1 bg-muted rounded-full">
            <button
              type="button"
              data-testid="level-debutant"
              onClick={() => setLevel('debutant')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
                level === 'debutant' ? 'bg-white dark:bg-red-900 text-red-700 dark:text-red-200 shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Débutant
            </button>
            <button
              type="button"
              data-testid="level-avance"
              onClick={() => setLevel('avance')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
                level === 'avance' ? 'bg-white dark:bg-red-900 text-red-700 dark:text-red-200 shadow-sm' : 'text-muted-foreground'
              }`}
            >
              Avancé
            </button>
          </div>

          <div className="rounded-xl bg-red-50 dark:bg-red-900/30 p-4">
            <p className="text-sm font-semibold text-red-900 dark:text-red-100 mb-1">Ton objectif</p>
            <p className="text-red-800 dark:text-red-200 font-medium" data-testid="challenge-objective">
              {level === 'avance' ? challenge.advanced_version : challenge.beginner_version}
            </p>
            <p className="text-xs text-red-700/80 dark:text-red-300/80 mt-2">
              🥗 Nutrition : {challenge.nutrition_action}
            </p>
          </div>

          {/* Proof upload */}
          <div>
            <p className="text-sm font-semibold mb-2 text-foreground">Ajoute ta preuve (optionnel)</p>
            {media ? (
              <div className="relative rounded-xl overflow-hidden border border-border">
                {media.media_type === 'video' ? (
                  <video src={mediaUrl(media.url)} className="w-full h-40 object-cover" controls />
                ) : (
                  <img src={mediaUrl(media.url)} alt="preuve" className="w-full h-40 object-cover" />
                )}
                <button
                  type="button"
                  onClick={() => setMedia(null)}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"
                  data-testid="remove-media"
                >
                  <X className="w-4 h-4" />
                </button>
                <Badge className="absolute bottom-2 left-2 bg-red-600 text-white">
                  {media.media_type === 'video' ? <Video className="w-3 h-3 mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                  {media.media_type === 'video' ? 'Vidéo (+20 pts)' : 'Photo (+15 pts)'}
                </Badge>
              </div>
            ) : (
              <button
                type="button"
                data-testid="upload-proof-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full border-2 border-dashed border-red-200 dark:border-red-800 rounded-xl py-6 flex flex-col items-center gap-2 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
              >
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
                <span className="text-sm font-medium">{uploading ? 'Envoi...' : 'Photo ou vidéo'}</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={handleFile} data-testid="proof-file-input" />
          </div>

          {/* Comment */}
          <div>
            <p className="text-sm font-semibold mb-2 text-foreground">Ton ressenti du jour</p>
            <Textarea
              data-testid="challenge-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comment t'es-tu senti(e) aujourd'hui ?"
              className="resize-none"
              rows={2}
            />
          </div>

          {/* Moods */}
          <div className="space-y-2.5">
            <p className="text-sm font-semibold text-foreground">Tes indicateurs (1 à 5)</p>
            {MOODS.map((m) => (
              <MoodRow
                key={m.key}
                label={m.label}
                testKey={m.key.replace('mood_', '')}
                value={moods[m.key] || 0}
                onChange={(v) => setMoods((s) => ({ ...s, [m.key]: v }))}
              />
            ))}
          </div>

          <Button
            onClick={handleValidate}
            disabled={saving}
            data-testid="confirm-validate-button"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6 text-base rounded-xl"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
            Je valide mon défi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
